import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableItem from './SortableItem';

const KanbanColumn = ({ stage, items, isOver, isSearching = false, searchQuery = '', isUpdating = false }) => {
  const { setNodeRef } = useDroppable({
    id: `column-${stage}`,
    data: {
      type: 'column',
      stage: stage
    }
  });

  // Highlight items that match search query
  const highlightSearchMatch = (text) => {
    if (!isSearching || !searchQuery || !text) {
      return text;
    }

    const regex = new RegExp(`(${searchQuery})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} style={{ 
          backgroundColor: 'rgba(var(--tblr-warning-rgb), 0.3)', 
          padding: '0 2px',
          borderRadius: '2px'
        }}>
          {part}
        </mark>
      ) : part
    );
  };

  return (
    <div
      ref={setNodeRef}
      className="kanban-column"
      style={{
        width: '320px',
        flexShrink: 0,
        transition: 'all 0.2s ease',
        background: isOver ? 'rgba(var(--tblr-primary-rgb), 0.04)' : 'transparent',
        borderRadius: '8px',
        padding: '8px',
        minHeight: '100px', // Ensure empty columns have height
        opacity: isSearching && items.length === 0 ? 0.5 : (isUpdating ? 0.7 : 1),
        pointerEvents: isUpdating ? 'none' : 'auto',
      }}
    >
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0">{stage}</h2>
        <div className="d-flex align-items-center gap-2">
          {isSearching && items.length > 0 && (
            <span className="badge bg-warning text-warning-fg badge-pill" style={{ fontSize: '10px' }}>
              {items.length} found
            </span>
          )}
          <span className={`badge badge-pill ${isSearching && items.length === 0 ? 'bg-secondary text-secondary-fg' : 'bg-blue text-blue-fg'}`}>
            {items.length}
          </span>
        </div>
      </div>
      <div className="border-top my-3"></div>
      <div
        className="kanban-items"
        style={{
          height: 'calc(100vh - 150px)',
          overflowY: 'auto',
          paddingRight: '8px',
          transition: 'background-color 0.2s ease',
        }}
      >
        <SortableContext items={items.map(item => item.id)} strategy={verticalListSortingStrategy}>
          {items.map((item) => (
            <SortableItem 
              key={item.id} 
              item={item} 
              isSearching={isSearching}
              searchQuery={searchQuery}
              highlightSearchMatch={highlightSearchMatch}
              isUpdating={isUpdating}
            />
          ))}
        </SortableContext>
        {items.length === 0 && (
          <div
            className="empty-column-placeholder"
            style={{
              padding: '20px',
              textAlign: 'center',
              color: '#aaa',
              backgroundColor: 'rgba(0,0,0,0.03)',
              borderRadius: '6px',
              border: '2px dashed rgba(0,0,0,0.1)',
              margin: '8px 0'
            }}
          >
            {isSearching ? (
              <div>
                <div>🔍 No matches found</div>
                <small className="text-muted">Try different search terms</small>
              </div>
            ) : (
              'Drop items here'
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default KanbanColumn; 